import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Pagination, Paper, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { MdArrowBack, MdCheckCircle, MdGridView, MdInventory2, MdPrint, MdRefresh, MdSearch, MdViewList } from 'react-icons/md';
import { getData, putData } from '../../Webservice/webservice';

const FONT = 'Mulish, sans-serif';
const authToken = () => { const raw = sessionStorage.getItem('token'); return raw ? `Bearer ${raw}` : ''; };
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' }, '&.Mui-disabled': { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'none' } };
const date = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS = {
  pending: { label: 'New', bg: '#fef3c7', fg: '#92400e', accent: '#f59e0b', hint: 'Waiting for you to accept' },
  accepted: { label: 'Accepted', bg: '#e0e7ff', fg: '#3730a3', accent: '#6366f1', hint: 'Pack it and print the invoice' },
  packed: { label: 'Packed', bg: '#e0f2fe', fg: '#075985', accent: '#0ea5e9', hint: 'Ready to send to the warehouse' },
  shipped: { label: 'On its way', bg: '#dbeafe', fg: '#1e40af', accent: '#3b82f6', hint: 'The warehouse will confirm when it arrives' },
  delivered: { label: 'Received', bg: '#dcfce7', fg: '#166534', accent: '#22c55e', hint: 'Received at the warehouse' },
  rejected: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444', hint: 'You turned this request down' },
};
const FLOW = ['pending', 'accepted', 'packed', 'shipped', 'delivered'];
const TABS = [['', 'All'], ...Object.keys(STATUS).map((k) => [k, STATUS[k].label])];

const StatusChip = ({ status }) => {
  const s = STATUS[status] || { label: status || '—', bg: '#f1f5f9', fg: '#475569' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 800, height: 24, background: s.bg, color: s.fg }} />;
};

// the API record, shaped for the page
const shape = (r) => ({
  requestId: r._id, id: r.receiptId, status: r.status, createdAt: r.createdAt, quantity: r.quantity, rejectionReason: r.rejectionReason,
  orderNumber: r.orderId?.orderNumber, etaDays: r.orderId?.etaDays, history: Array.isArray(r.statusHistory) ? r.statusHistory : [],
  product: r.product_id || {}, warehouse: r.orderId?.warehouseID || {},
});

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.85, borderBottom: '1px dashed #e2e8f0', '&:last-of-type': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', flexShrink: 0 }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word', textTransform: 'capitalize' }}>{value || '—'}</Typography>
  </Box>
);
const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25, minWidth: 0 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, mb: 0.75 }}>{title}</Typography>
    {children}
  </Box>
);

const OnlineOrders = () => {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [view, setView] = useState('grid');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // list
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  // one order
  const [order, setOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { status, title, text, button }
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  useEffect(() => { const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('seller/request/list', { page, limit: 20, ...(status && { status }), ...(debounced && { search: debounced }) }, authToken());
      setOrders((res?.requests || []).map(shape));
      setStats(res?.stats || {});
      setMeta(res?.meta || { total: 0, totalPages: 1 });
      setError('');
    } catch (e) {
      setError(e?.message || 'Could not load your orders.');
      setOrders([]);
    } finally { setLoading(false); }
  }, [page, status, debounced]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadOrder = useCallback(async () => {
    if (!requestId) { setOrder(null); return; }
    setDetailLoading(true); setDetailError('');
    try {
      setOrder(shape(await getData(`seller/request/${requestId}`, null, authToken())));
    } catch (e) {
      setOrder(null);
      setDetailError(e?.message || 'This order could not be found.');
    } finally { setDetailLoading(false); }
  }, [requestId]);
  useEffect(() => { loadOrder(); }, [loadOrder]);

  const move = async (next, extra = {}) => {
    setBusy(true); setError('');
    try {
      await putData(`seller/request/update/${order.requestId}`, { status: next, ...extra }, authToken());
      setNotice(next === 'rejected' ? 'The request was rejected.' : `Marked as ${STATUS[next].label.toLowerCase()}.`);
      setConfirm(null); setRejectOpen(false); setReason('');
      await loadOrder();
      loadList();
    } catch (e) {
      setError(e?.message || 'Could not update the order.');
      setConfirm(null);
    } finally { setBusy(false); }
  };

  const submitReject = () => {
    if (reason.trim().length < 5) return setReasonError('Tell us why, in at least a few words.');
    move('rejected', { rejectionReason: reason.trim() });
  };

  // open the window first so the browser does not block it, then send it to the PDF
  const printDoc = async (path, what) => {
    const w = window.open('', '_blank');
    setBusy(true); setError('');
    try {
      const res = await putData(`seller/request/${path}/${order.requestId}`, {}, authToken());
      if (!res?.pdfPath) throw new Error(`Could not make the ${what}.`);
      if (w) w.location.href = res.pdfPath; else window.location.href = res.pdfPath;
    } catch (e) {
      if (w) w.close();
      setError(e?.message || `Could not make the ${what}.`);
    } finally { setBusy(false); }
  };

  // ---------------------------------------------------------------- one order
  if (requestId) {
    if (detailLoading && !order) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={90} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={380} sx={{ borderRadius: '14px' }} /></Box>;
    if (detailError || !order) {
      return (
        <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{detailError || 'This order could not be found.'}</Alert>
          <Button onClick={() => navigate('/orders')} sx={yellowBtn}>Back to orders</Button>
        </Box>
      );
    }
    const st = STATUS[order.status] || STATUS.pending;
    const step = FLOW.indexOf(order.status);
    const w = order.warehouse;
    const p = order.product;
    const actions = {
      pending: [['Accept order', () => setConfirm({ status: 'accepted', title: 'Accept this order?', text: 'You are agreeing to supply it to the warehouse.', button: 'Accept' }), true], ['Reject', () => { setRejectOpen(true); setReason(''); setReasonError(''); }, false]],
      accepted: [['Print invoice', () => printDoc('print', 'invoice'), false, <MdPrint />], ['Mark as packed', () => setConfirm({ status: 'packed', title: 'Mark as packed?', text: 'Do this once the goods are packed and the invoice is ready.', button: 'Mark packed' }), true], ['Can’t supply', () => { setRejectOpen(true); setReason(''); setReasonError(''); }, false]],
      packed: [['Print label', () => printDoc('print/del', 'label'), false, <MdPrint />], ['Mark as shipped', () => setConfirm({ status: 'shipped', title: 'Mark as shipped?', text: 'Do this once the parcel has left you for the warehouse.', button: 'Mark shipped' }), true]],
    }[order.status] || [];

    return (
      <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <IconButton onClick={() => navigate('/orders')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Order #{order.id}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{order.orderNumber ? `Customer order ${order.orderNumber} · ` : ''}requested {date(order.createdAt)}</Typography>
          </Box>
          <StatusChip status={order.status} />
        </Box>

        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* progress */}
        <Box sx={{ p: 2, mb: 2, borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0' }}>
          {order.status === 'rejected' ? (
            <Alert severity="error"><b>Rejected.</b> {order.rejectionReason || 'No reason was recorded.'}</Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {FLOW.map((k, i) => (
                  <React.Fragment key={k}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 64 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800, background: i <= step ? st.accent : '#e2e8f0', color: i <= step ? '#fff' : '#94a3b8' }}>{i < step ? <MdCheckCircle size={18} /> : i + 1}</Box>
                      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: i === step ? 800 : 600, color: i <= step ? '#0f172a' : '#94a3b8', textAlign: 'center' }}>{STATUS[k].label}</Typography>
                    </Box>
                    {i < FLOW.length - 1 && <Box sx={{ flex: 1, height: 3, borderRadius: 2, background: i < step ? st.accent : '#e2e8f0', mt: -2.2 }} />}
                  </React.Fragment>
                ))}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', mt: 1.25, textAlign: 'center' }}>{st.hint}</Typography>
            </>
          )}
        </Box>

        {actions.length > 0 && (
          <Box data-tour="vendor-order-actions" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {actions.map(([label, onClick, primary, icon]) => (
              <Button key={label} onClick={onClick} disabled={busy} startIcon={icon} sx={primary ? yellowBtn : { textTransform: 'none', fontWeight: 700, borderRadius: '10px', border: '1px solid #cbd5e1', color: label === 'Reject' || label.startsWith('Can') ? '#dc2626' : '#0f172a', px: 2.5 }}>{label}</Button>
            ))}
            {busy && <CircularProgress size={22} sx={{ alignSelf: 'center' }} />}
          </Box>
        )}

        <Box data-tour="vendor-order-details" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
          <Card title="Product" accent="#6366f1">
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
              <Avatar variant="rounded" src={p.product_images?.[0]} sx={{ width: 64, height: 64 }}><MdInventory2 /></Avatar>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>{p.title || 'Product'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{p.brand}{p.upc ? ` · UPC ${p.upc}` : ''}</Typography>
              </Box>
            </Box>
            <Row label="Quantity to send" value={`${order.quantity || 0} units`} />
            <Row label="Pack size" value={p.unitValue ? `${p.unitValue} ${p.unitName || ''}` : null} />
            <Row label="Container" value={p.container_type} />
            <Row label="Free product" value={p.isFree ? 'Yes' : null} />
          </Card>

          <Card title="Send to the warehouse" accent="#0ea5e9">
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{w.name || 'Warehouse'}{w.code ? ` (${w.code})` : ''}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#334155', mb: 1, textTransform: 'none' }}>{[w.address, w.city, w.state, w.pincode, w.country].filter(Boolean).join(', ') || 'Address not available'}</Typography>
            <Row label="Contact person" value={w.contactPerson} />
            <Row label="Phone" value={w.phoneNumber} />
            <Row label="Email" value={w.email} />
            <Row label="Expected delivery" value={order.etaDays ? `Within ${order.etaDays} day${order.etaDays > 1 ? 's' : ''}` : null} />
          </Card>

          <Card title="History" accent="#22c55e">
            {order.history.filter((h) => h.isActive !== false).length === 0 ? (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', py: 1 }}>Nothing recorded yet.</Typography>
            ) : order.history.filter((h) => h.isActive !== false).map((h, i) => (
              <Box key={`${h.status}-${i}`} sx={{ display: 'flex', gap: 1.25, py: 0.9, borderBottom: '1px dashed #e2e8f0', '&:last-of-type': { borderBottom: 'none' } }}>
                <StatusChip status={h.status} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#334155' }}>{h.note || STATUS[h.status]?.hint}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{dateTime(h.changedAt)}</Typography>
                </Box>
              </Box>
            ))}
          </Card>
        </Box>

        {/* confirm a step */}
        <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>{confirm?.title}</DialogTitle>
          <DialogContent><Typography sx={{ fontFamily: FONT, fontSize: 14 }}>{confirm?.text}</Typography></DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Not yet</Button>
            <Button onClick={() => move(confirm.status)} disabled={busy} sx={yellowBtn}>{busy ? 'Saving…' : confirm?.button}</Button>
          </DialogActions>
        </Dialog>

        {/* reject */}
        <Dialog open={rejectOpen} onClose={() => !busy && setRejectOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>{order.status === 'accepted' ? 'You can’t supply this?' : 'Reject this order?'}</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b', mb: 1.5 }}>Order #{order.id}. The team sees your reason, and this cannot be undone.</Typography>
            <TextField autoFocus fullWidth multiline minRows={3} label="Reason *" value={reason} onChange={(e) => { setReason(e.target.value); setReasonError(''); }} error={!!reasonError} helperText={reasonError || ' '} inputProps={{ maxLength: 300 }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRejectOpen(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button onClick={submitReject} disabled={busy} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 800 }}>{busy ? 'Saving…' : 'Reject order'}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ---------------------------------------------------------------- the list
  const open = (o) => navigate(`/orders/${o.requestId}`);
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Online orders</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Customer orders you need to send to the warehouse.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup data-tour="vendor-order-view" value={view} exclusive size="small" onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="grid" aria-label="cards"><MdGridView size={18} /></ToggleButton>
            <ToggleButton value="table" aria-label="table"><MdViewList size={18} /></ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh"><IconButton onClick={loadList} sx={{ background: '#fff', border: '1px solid #e2e8f0' }}><MdRefresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {stats.pending > 0 && status !== 'pending' && <Alert severity="info" sx={{ mb: 2, fontWeight: 600 }} action={<Button color="inherit" size="small" onClick={() => { setStatus('pending'); setPage(1); }}>Show them</Button>}>{stats.pending} new order{stats.pending > 1 ? 's are' : ' is'} waiting for you.</Alert>}

      <Box data-tour="vendor-order-status" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {TABS.map(([k, label]) => {
          const on = status === k;
          const color = STATUS[k]?.accent || '#0f172a';
          const n = k ? stats[k] : stats.total;
          return (
            <Chip key={label} clickable onClick={() => { setStatus(k); setPage(1); }} label={`${label} · ${n || 0}`}
              sx={{ fontFamily: FONT, fontWeight: 800, borderRadius: '10px', color: on ? '#fff' : color, background: on ? color : `${color}18`, border: '1px solid', borderColor: on ? color : `${color}55`, '&:hover': { background: on ? color : `${color}30` } }} />
          );
        })}
      </Box>

      <TextField data-tour="vendor-order-search" size="small" fullWidth placeholder="Search order request, customer order number or product…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, background: '#fff', borderRadius: '10px' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />

      {loading ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={170} sx={{ borderRadius: '14px' }} />)}</Box>
      ) : orders.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
          <MdInventory2 size={38} color="#cbd5e1" />
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, mt: 1 }}>{debounced ? 'No order matches your search' : status ? `No ${STATUS[status].label.toLowerCase()} orders` : 'No orders yet'}</Typography>
          {(debounced || status) && <Button onClick={() => { setSearch(''); setStatus(''); }} sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>Show all orders</Button>}
        </Box>
      ) : view === 'grid' ? (
        <Box data-tour="vendor-order-list" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
          {orders.map((o) => {
            const st = STATUS[o.status] || {};
            return (
              <Box key={o.requestId} onClick={() => open(o)} sx={{ cursor: 'pointer', p: 2, borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0', borderLeft: `5px solid ${st.accent || '#94a3b8'}`, transition: 'all .2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 24px rgba(15,23,42,.08)' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 13, color: '#4f46e5' }}>#{o.id}</Typography>
                  <StatusChip status={o.status} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                  <Avatar variant="rounded" src={o.product.product_images?.[0]} sx={{ width: 52, height: 52 }}><MdInventory2 /></Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{o.product.title || 'Product'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{o.quantity} unit{o.quantity === 1 ? '' : 's'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>To {o.warehouse.name || 'warehouse'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{date(o.createdAt)}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ background: '#f8fafc' }}>{['Request', 'Product', 'Units', 'Warehouse', 'Requested', 'Status'].map((h) => <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12, color: '#64748b' }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.requestId} hover onClick={() => open(o)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: 12.5, color: '#4f46e5' }}>#{o.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                        <Avatar variant="rounded" src={o.product.product_images?.[0]} sx={{ width: 34, height: 34 }}><MdInventory2 size={16} /></Avatar>
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{o.product.title || 'Product'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, fontWeight: 700 }}>{o.quantity}</TableCell>
                    <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>{o.warehouse.name || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{date(o.createdAt)}</TableCell>
                    <TableCell><StatusChip status={o.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {meta.totalPages > 1 && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}><Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} orders</Typography><Pagination count={meta.totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" /></Box>}
    </Box>
  );
};

export default OnlineOrders;
